import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getTenantSettings } from '../../config/tenant-settings.registry';

export interface AppSettings {
  appName?: string;
  companyDisplayName?: string;
  logoUrl?: string;
  logoAlt?: string;
  primaryColor?: string;
  secondaryColor?: string;
  primaryTextColor?: string;
  enableUserDataRequests?: boolean;
}

const LOCAL_STORAGE_KEY = 'tbds.appSettings';
const ORG_BRANDING_CACHE_KEY = 'tbds.orgBranding';

type OrgBranding = {
  orgId: string;
  displayName?: string | null;
  logoUrl?: string | null;
  logoDataUrl?: string | null;
  logoSourceUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class AppSettingsService {
  private readonly settingsSubject = new BehaviorSubject<AppSettings | null>(null);
  readonly settings$: Observable<AppSettings | null> = this.settingsSubject.asObservable();
  private baseSettings: AppSettings = {};
  private activeOrgId: string | null = null;

  constructor() {
    this.loadFromCache();
    this.loadFromSource();
  }

  private loadFromCache(): void {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as AppSettings;
      this.settingsSubject.next(parsed);
      this.applyTheming(parsed);
    } catch {
      // Ignore cache errors
    }
  }

  private loadFromSource(): void {
    try {
      const sourceSettings = getTenantSettings(environment.tenantKey);
      const normalized = this.normalizeSettings(sourceSettings);
      this.baseSettings = normalized;
      const cached = this.settingsSubject.value;
      if (cached) {
        const merged = { ...normalized, ...cached };
        this.settingsSubject.next(merged);
        this.applyTheming(merged);
        return;
      }
      this.settingsSubject.next(normalized);
      this.saveToCache(normalized);
      this.applyTheming(normalized);
    } catch (error) {
      console.error('[AppSettingsService] Failed to load tenant settings from source:', error);
    }
  }

  private saveToCache(settings: AppSettings): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Ignore cache errors
    }
  }

  private applyTheming(settings: AppSettings): void {
    const root = document.documentElement;
    if (settings.primaryColor) {
      root.style.setProperty('--tbds-primary', settings.primaryColor);
    }
    if (settings.secondaryColor) {
      root.style.setProperty('--tbds-secondary', settings.secondaryColor);
    }
    if (settings.primaryTextColor) {
      root.style.setProperty('--tbds-primary-text', settings.primaryTextColor);
    }
  }

  hasCachedBranding(orgId: string | null | undefined): boolean {
    const cleanedOrgId = String(orgId || '').trim();
    if (!cleanedOrgId) {
      return false;
    }
    const cache = this.loadOrgBrandingCache();
    return Boolean(cache[cleanedOrgId]);
  }

  applyOrganizationBranding(branding: OrgBranding | null | undefined): void {
    const cleanedOrgId = String(branding?.orgId || '').trim();
    if (!cleanedOrgId) {
      return;
    }
    this.activeOrgId = cleanedOrgId;
    const cache = this.loadOrgBrandingCache();
    const incomingLogoUrl = String(branding?.logoUrl || '').trim() || null;
    const existing = cache[cleanedOrgId];
    const canReuseCachedLogo =
      Boolean(existing?.logoDataUrl) &&
      Boolean(existing?.logoSourceUrl) &&
      existing?.logoSourceUrl === incomingLogoUrl;
    cache[cleanedOrgId] = {
      orgId: cleanedOrgId,
      displayName: branding?.displayName || null,
      logoUrl: incomingLogoUrl,
      logoDataUrl: canReuseCachedLogo ? existing?.logoDataUrl || null : null,
      logoSourceUrl: canReuseCachedLogo ? existing?.logoSourceUrl || null : null,
      primaryColor: branding?.primaryColor || null,
      secondaryColor: branding?.secondaryColor || null,
    };
    this.saveOrgBrandingCache(cache);
    const merged = this.mergeWithBase(cache[cleanedOrgId]);
    this.settingsSubject.next(merged);
    this.saveToCache(merged);
    this.applyTheming(merged);
    void this.hydrateLogoCache(cleanedOrgId, cache[cleanedOrgId]);
  }

  private mergeWithBase(branding: OrgBranding): AppSettings {
    const resolvedLogoUrl =
      String(branding.logoDataUrl || '').trim() || String(branding.logoUrl || '').trim() || this.baseSettings.logoUrl;
    return {
      ...this.baseSettings,
      companyDisplayName: String(branding.displayName || '').trim() || this.baseSettings.companyDisplayName,
      logoUrl: resolvedLogoUrl,
      primaryColor: String(branding.primaryColor || '').trim() || this.baseSettings.primaryColor,
      secondaryColor: String(branding.secondaryColor || '').trim() || this.baseSettings.secondaryColor,
    };
  }

  private async hydrateLogoCache(orgId: string, branding: OrgBranding): Promise<void> {
    const logoUrl = String(branding.logoUrl || '').trim();
    if (!logoUrl) {
      return;
    }
    // Fetch-based byte caching requires CORS; skip cross-origin URLs to avoid noisy browser CORS failures.
    if (!this.isSameOriginOrRelativeUrl(logoUrl)) {
      return;
    }
    const cachedDataUrl = String(branding.logoDataUrl || '').trim();
    const cachedSourceUrl = String(branding.logoSourceUrl || '').trim();
    if (cachedDataUrl && cachedSourceUrl === logoUrl) {
      return;
    }
    try {
      const response = await fetch(logoUrl, { cache: 'force-cache' });
      if (!response.ok) {
        return;
      }
      const blob = await response.blob();
      const dataUrl = await this.blobToDataUrl(blob);
      const cache = this.loadOrgBrandingCache();
      const latest = cache[orgId];
      if (!latest) {
        return;
      }
      const latestLogoUrl = String(latest.logoUrl || '').trim();
      if (!latestLogoUrl || latestLogoUrl !== logoUrl) {
        return;
      }
      latest.logoDataUrl = dataUrl;
      latest.logoSourceUrl = logoUrl;
      cache[orgId] = latest;
      this.saveOrgBrandingCache(cache);
      if (this.activeOrgId === orgId) {
        const merged = this.mergeWithBase(latest);
        this.settingsSubject.next(merged);
        this.saveToCache(merged);
      }
    } catch {
      // Ignore logo cache hydration failures
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const value = typeof reader.result === 'string' ? reader.result : '';
        if (value) {
          resolve(value);
          return;
        }
        reject(new Error('Failed to convert blob to data URL'));
      };
      reader.onerror = () => reject(reader.error || new Error('FileReader error'));
      reader.readAsDataURL(blob);
    });
  }

  private isSameOriginOrRelativeUrl(url: string): boolean {
    if (!url) {
      return false;
    }
    if (url.startsWith('/')) {
      return true;
    }
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private loadOrgBrandingCache(): Record<string, OrgBranding> {
    try {
      const raw = localStorage.getItem(ORG_BRANDING_CACHE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw) as Record<string, OrgBranding>;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }
      return parsed;
    } catch {
      return {};
    }
  }

  private saveOrgBrandingCache(cache: Record<string, OrgBranding>): void {
    try {
      localStorage.setItem(ORG_BRANDING_CACHE_KEY, JSON.stringify(cache));
    } catch {
      // Ignore cache errors
    }
  }

  private normalizeSettings(raw: any): AppSettings {
    // Expect a flat API payload; just cast and return.
    return (raw || {}) as AppSettings;
  }
}

